import { useEffect } from 'react';

interface CustomModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  type?: 'alert' | 'confirm' | 'warning';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const CustomModal = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'alert',
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
}: CustomModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  const getTitleColor = () => {
    switch (type) {
      case 'warning':
        return 'text-yellow-400';
      case 'confirm':
        return 'text-white';
      default:
        return 'text-white';
    }
  };

  const getConfirmButtonColor = () => {
    switch (type) {
      case 'warning':
        return 'bg-red-500 hover:bg-red-600 text-white';
      case 'confirm':
        return 'bg-primary text-black hover:opacity-90';
      default:
        return 'bg-primary text-black hover:opacity-90';
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-[#1a1a1a] rounded-xl border border-gray-800 shadow-2xl w-[90%] max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h3 className={`text-lg font-bold ${getTitleColor()}`}>
            {title || (type === 'warning' ? 'Warning' : type === 'confirm' ? 'Confirm' : 'Alert')}
          </h3>
          {type === 'alert' && (
            <button
              onClick={onClose}
              className="text-tg-hint hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="p-4">
          <p className="text-white text-base leading-relaxed">{message}</p>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-800">
          {type === 'confirm' || type === 'warning' ? (
            <>
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2.5 rounded-lg bg-tg-secondary text-tg-hint hover:bg-[#2a2a2a] hover:text-white transition-colors font-semibold"
              >
                {cancelText || 'Cancel'}
              </button>
              <button
                onClick={handleConfirm}
                className={`flex-1 px-4 py-2.5 rounded-lg font-semibold transition-all ${getConfirmButtonColor()}`}
              >
                {confirmText || (type === 'warning' ? 'Close anyway' : 'Confirm')}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 rounded-lg bg-primary text-black hover:opacity-90 font-semibold transition-all"
            >
              {confirmText || 'OK'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomModal;

